import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Router, { useRouter } from 'next/router';
import { FORM_ERROR } from 'final-form';
import { Form } from 'react-final-form';
import { submitWebform } from '../../../api/webform';
import getForm from '../../../utils/transforms.webform';
import Button from '../../01_atoms/Button';
import FormattedText from '../../01_atoms/FormattedText';
import { toast } from 'react-toastify';
import ReCaptchaGoogle from '../../01_atoms/ReCaptchaGoogle';

const debug = require('debug')('cw:webform');

import styles from './index.module.scss';

const Webform = ({
  webform: { id, fields, successType, successMessage, successUrl },
  reCaptchaEnabled = false,
}) => {
  const reCaptchaRef = useRef();
  const router = useRouter();
  // ForceCaptcha parameter could be used by test suite in case ReCAPTCHA is disabled
  // on the site settings level.
  const forceCaptcha = parseInt(router.query.forceCaptcha, 10);
  // Don't load captcha on the frontend if testSuite parameter was passed.
  const isTestSuite = router.query['test-suite'] === 'true';
  const [isCaptchaExecuting, setCaptchaExecuting] = useState(false);
  const [reCaptchaToken, setReCaptchaToken] = useState('');

  const onSubmit = async (values, form) => {
    // Forbid execution if captcha is executing and token isn't received yet. It helps to avoid
    // sending form submission requests without captcha tokens that happens if the speed of connection
    // is slow and a user has ability to hit "Continue" several times before the captcha popup
    // loads and appears.
    if (isCaptchaExecuting && !reCaptchaToken) {
      return {};
    }

    if (
      !reCaptchaToken &&
      !isCaptchaExecuting &&
      (forceCaptcha || (!isTestSuite && reCaptchaEnabled))
    ) {
      setCaptchaExecuting(true);
      setReCaptchaToken('');
      reCaptchaRef.current.execute();
      return {};
    }

    const data = { webform_id: id, draft: false, captcha_token: reCaptchaToken };

    // Prepare webform data for post request.
    Object.keys(values).forEach((key) => {
      if (values[key] === false || values[key] === null) {
        // Case for single checkboxes. Anything apart from null is treated
        // as checked by webform validator.
        data[key] = null;
      } else if (typeof values[key] === 'object' && !Array.isArray(values[key])) {
        data[key] =
          typeof values[key].value === 'string' ? values[key].value.trim() : values[key].value;
      } else {
        data[key] = typeof values[key] === 'string' ? values[key].trim() : values[key];
      }
    });

    debug('Submitting webform data %o', data);

    // Make sure no old alerts are hanging around.
    toast.dismiss();
    const result = await submitWebform(data);

    if (result.errorMessage) {
      setCaptchaExecuting(false);
      setReCaptchaToken('');
      toast.error(result.errorMessage);
      // We return generic form error so that the form state
      // (submitFailed and submitSucceeded) have correct values.
      return { [FORM_ERROR]: result.errorMessage };
    }

    // If the type of form success behavior is redirect,
    // then we don't show any alert message nor resetting form
    // fields to avoid confusing behavior when the form values
    // have been cleaned up, but the redirect still didn't happen.
    // Especially can be confusing on the slow devices / connection.
    if (successType === 'redirect' && successUrl.length) {
      Router.push(successUrl);
      return {};
    }

    // Resetting the form after a successful submission.
    // We use setTimeout here as the library recommends
    // /node_modules/final-form/dist/final-form.es.js:1091
    // https://www.pivotaltracker.com/story/show/166843750.
    setTimeout(form.reset, 100);

    // Reset form fields, so that after successful submit
    // they don't look like invalidated.
    // https://www.pivotaltracker.com/story/show/176920360.
    Object.keys(values).forEach((key) => {
      form.change(key, undefined);
      form.resetFieldState(key);
    });

    toast.success(<FormattedText text={successMessage} />, { autoClose: 10000 });
    setReCaptchaToken('');
    setCaptchaExecuting(false);

    return {};
  };

  const handleCaptchaChallenge = (token, handleSubmit) => {
    try {
      setCaptchaExecuting(false);
      setReCaptchaToken(token);
      // Wait when state for the component is updated and Submit the whole form.
      setTimeout(() => {
        handleSubmit();
      }, 100);

      // As soon as the form is submitted we want to reset the captcha
      // challenge, so that the next form submit (i.e. in case of error)
      // will execute a new challenge with the new token. Otherwise
      // Google will complain on duplicated tokens sent multiple times.
      reCaptchaRef.current.reset();
    } catch (e) {
      debug('Could not handle submission with captcha: %O', e);
    }
  };

  const handleCaptchaError = () => {
    setCaptchaExecuting(false);
    setReCaptchaToken('');
    reCaptchaRef.current.reset();
  };

  const formComponents = getForm(fields);

  return (
    <Form
      onSubmit={onSubmit}
      subscription={{
        submitting: true,
        submitFailed: true,
        submitSucceeded: true,
        hasValidationErrors: true,
      }}
      initialValues={formComponents.initialValues}
      mutators={{
        // Create a mutator which allows to modify field's value by external callbacks.
        setValue: ([field, value], state, { changeValue }) => {
          changeValue(state, field, () => value);
        },
      }}
      render={({
        handleSubmit,
        submitting,
        submitFailed,
        submitSucceeded,
        hasValidationErrors,
      }) => (
        <form id={id} onSubmit={handleSubmit} className={styles['webform']}>
          {formComponents.components.map((item) => {
            const Component = item.component;
            return <Component key={item.props.name} {...item.props} />;
          })}
          {(forceCaptcha || (!isTestSuite && reCaptchaEnabled)) && (
            <ReCaptchaGoogle
              forwardedRef={reCaptchaRef}
              onCaptchaChallenge={(token) => handleCaptchaChallenge(token, handleSubmit)}
              onCaptchaError={handleCaptchaError}
            />
          )}
          <Button
            type="primary"
            // We show loading button in case when the form either being submitted,
            // or the response from the API was successful and we're waiting for
            // redirect to happen to the Thank You page (for better UX).
            // See https://www.pivotaltracker.com/story/show/176920360.
            isLoading={
              submitting || (successType === 'redirect' && !!successUrl.length && submitSucceeded)
            }
            disabled={
              submitting || (successType === 'redirect' && !!successUrl.length && submitSucceeded)
            }
            className={hasValidationErrors ? 'button-disabled' : ''}
          >
            {formComponents.submitButtonLabel || 'Submit'}
          </Button>
          {submitFailed && hasValidationErrors && (
            <span data-component="field-error">
              Some form fields are filled incorrectly. Please fix them and submit the form.
            </span>
          )}
        </form>
      )}
    />
  );
};

Webform.propTypes = {
  webform: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        field_id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      }),
    ).isRequired,
    successType: PropTypes.oneOf(['redirect', 'message']).isRequired,
    successMessage: PropTypes.string.isRequired,
    successUrl: PropTypes.string.isRequired,
  }).isRequired,
  reCaptchaEnabled: PropTypes.bool,
};

export default Webform;
